import { useEffect } from "react";
import { send_request } from "../../../utils/Request";
import useAgentStore from "../useAgentStore";
import {
  agentDraftFromApi,
  normalizeReviewTaskUsers,
} from "./agentConfigDraftModel";
import { KNOWLEDGE_INDEX_DEFAULT_STATUS } from "./knowledgeStatus";
import { mapToolCatalogToCategories } from "./toolCatalog";

const mapDatabaseTables = (res) => {
  const v2 = Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res)
    ? res
    : [];
  return v2.map((t) => ({
    databaseId: t.tableId,
    tableId: t.tableId,
    name: t.tableName,
    tableName: t.tableName,
  }));
};

export function useAgentConfigBootstrap({
  open,
  agentId,
  loaded,
  applyDraftToState,
  setLoaded,
  setPublicShareLink,
  setPublicShareLinkError,
  loadLatestPublicShareLink,
  setKnowledgeIndexStatus,
  setLoadingToolCatalog,
  setToolCategories,
  setLoadingWorkflowOptions,
  setWorkflowOptions,
  setLoadingAgentOptions,
  setAgentOptions,
  setGlobalVarPlaceholders,
  setLoadingDatabases,
  setDatabaseList,
  setLoadingReviewTaskEligibleUsers,
  setReviewTaskEligibleUsers,
  setAllAgents,
  setEquippedAgentFunctionTools,
  setAgentFunctionToolMeta,
}) {
  useEffect(() => {
    let ignore = false;
    if (open && !loaded) {
      if (agentId) {
        useAgentStore
          .getState()
          .fetchAgent(agentId)
          .then((data) => {
            if (ignore) return;
            if (data) {
              applyDraftToState(agentDraftFromApi(data));
              setPublicShareLinkError("");
              void loadLatestPublicShareLink({ ignore: () => ignore });
            } else {
              setPublicShareLink(null);
              setPublicShareLinkError("");
              setKnowledgeIndexStatus({ ...KNOWLEDGE_INDEX_DEFAULT_STATUS });
            }
            setLoaded(true);
          });
      } else {
        setLoaded(true);
      }
    }
    return () => {
      ignore = true;
    };
  }, [
    open,
    agentId,
    loaded,
    applyDraftToState,
    loadLatestPublicShareLink,
    setKnowledgeIndexStatus,
    setLoaded,
    setPublicShareLink,
    setPublicShareLinkError,
  ]);

  useEffect(() => {
    let ignore = false;
    if (open) {
      setLoadingToolCatalog(true);
      setLoadingWorkflowOptions(true);
      setLoadingAgentOptions(true);
      setLoadingDatabases(true);
      setLoadingReviewTaskEligibleUsers(true);

      // V2 agent (custom) functions: published functions appear in the picker's
      // "Custom Functions" category; per-agent bindings (plus tenant-wide allAgents) drive
      // the equipped state. Toggling routes to the bindings API, not enabledTools
      // (the runtime reads bindings, so enabledTools is never the source of truth
      // for these). The published list is fetched for both new and existing
      // agents so custom tools are always visible in the picker; only the
      // per-agent bindings require an id (a brand-new agent has none yet, so its
      // equipped set starts from allAgents functions only and is applied on save).
      const publishedPromise = send_request(
        "ai-service/agent-functions",
        "",
        {},
        "GET"
      )
        .then((listRes) => {
          const all = Array.isArray(listRes?.data) ? listRes.data : [];
          return all.filter((fn) => fn?.status === "PUBLISHED");
        })
        .catch(() => []);
      const boundIdsPromise = agentId
        ? send_request(
            `ai-service/agent-functions/agents/${agentId}`,
            "",
            {},
            "GET"
          )
            .then((boundRes) => {
              const bound = Array.isArray(boundRes?.data) ? boundRes.data : [];
              return new Set(bound.map((fn) => fn?.id).filter(Boolean));
            })
            .catch(() => new Set())
        : Promise.resolve(new Set());
      const agentFunctionsPromise = Promise.all([
        publishedPromise,
        boundIdsPromise,
      ]).then(([published, boundIds]) => ({ published, boundIds }));

      const toolCatalogPromise = Promise.all([
        useAgentStore
          .getState()
          .fetchToolCatalog()
          .catch(() => []),
        agentFunctionsPromise,
      ])
        .then(([catalog, { published, boundIds }]) => {
          const fnEntries = published.map((fn) => ({
            toolName: `agent_fn_${fn.slug}`,
            displayName: fn.displayName || fn.slug,
            description: fn.description || "",
            // Matches AgentFunctionToolAdapter.CATEGORY / AgentToolCategoryOverrides so
            // runtime agent functions group under the same heading as the authoring tools.
            category: "Custom Functions",
            // allAgents functions are auto-available to every agent; show them
            // equipped but non-toggleable (you turn that off on the function).
            disableStandaloneToggle: Boolean(fn.allAgents),
          }));
          const categories = mapToolCatalogToCategories([
            ...(Array.isArray(catalog) ? catalog : []),
            ...fnEntries,
          ]);
          const meta = {};
          const equipped = [];
          published.forEach((fn) => {
            const name = `agent_fn_${fn.slug}`;
            meta[name] = {
              functionId: fn.id,
              slug: fn.slug,
              allAgents: Boolean(fn.allAgents),
            };
            if (fn.allAgents || boundIds.has(fn.id)) equipped.push(name);
          });
          return { categories, meta, equipped };
        })
        .catch(() => ({ categories: [], meta: {}, equipped: [] }));
      const workflowOptionsPromise = useAgentStore
        .getState()
        .fetchWorkflowOptions()
        .then((options) => (Array.isArray(options) ? options : []))
        .catch(() => []);
      const agentOptionsPromise = send_request(
        "project-service/agent/list",
        "",
        {},
        "GET"
      )
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          return list.filter((agent) => agent?.systemAgent !== true);
        })
        .catch(() => []);
      const globalVarsPromise = send_request(
        "component-service/global-variables",
        "",
        {},
        "get"
      )
        .then((res) => {
          const vars = res?.data || [];
          return vars.map((v) => ({ key: `${v.name}.GLOBAL`, type: "text" }));
        })
        .catch(() => []);
      const databaseListPromise = send_request(
        "data-service/v2/table",
        "",
        {},
        "GET"
      )
        .then(mapDatabaseTables)
        .catch(() => []);
      const reviewUsersPromise = send_request(
        "authz-service/get-active-users",
        "",
        "",
        "get"
      )
        .then((res) => normalizeReviewTaskUsers(res?.data))
        .catch(() => []);
      const allAgentsPromise = useAgentStore
        .getState()
        .refreshAgents()
        .then(() =>
          useAgentStore
            .getState()
            .agents.filter((agent) => agent.agentId !== agentId)
        )
        .catch(() => []);

      Promise.allSettled([
        toolCatalogPromise,
        workflowOptionsPromise,
        agentOptionsPromise,
        globalVarsPromise,
        databaseListPromise,
        reviewUsersPromise,
        allAgentsPromise,
      ]).then((results) => {
        if (ignore) return;
        const valueAt = (index, fallback) =>
          results[index]?.status === "fulfilled"
            ? results[index].value
            : fallback;
        const toolCatalogResult = valueAt(0, {
          categories: [],
          meta: {},
          equipped: [],
        });
        setToolCategories(toolCatalogResult.categories || []);
        setAgentFunctionToolMeta(toolCatalogResult.meta || {});
        setEquippedAgentFunctionTools(toolCatalogResult.equipped || []);
        setWorkflowOptions(valueAt(1, []));
        setAgentOptions(valueAt(2, []));
        setGlobalVarPlaceholders(valueAt(3, []));
        setDatabaseList(valueAt(4, []));
        setReviewTaskEligibleUsers(valueAt(5, []));
        setAllAgents(valueAt(6, []));
        setLoadingToolCatalog(false);
        setLoadingWorkflowOptions(false);
        setLoadingAgentOptions(false);
        setLoadingDatabases(false);
        setLoadingReviewTaskEligibleUsers(false);
      });
    }
    return () => {
      ignore = true;
    };
  }, [
    open,
    agentId,
    setAgentOptions,
    setAllAgents,
    setDatabaseList,
    setGlobalVarPlaceholders,
    setLoadingAgentOptions,
    setLoadingDatabases,
    setLoadingReviewTaskEligibleUsers,
    setLoadingToolCatalog,
    setLoadingWorkflowOptions,
    setReviewTaskEligibleUsers,
    setToolCategories,
    setWorkflowOptions,
    setAgentFunctionToolMeta,
    setEquippedAgentFunctionTools,
  ]);
}
